import { useEffect, useState } from "react";
import {
    Bug,
    Search,
    ShieldX,
    X,
} from "lucide-react";

import { CVEDetailDialog } from "~/components/CVEDetailDialog";
import { Pagination } from "~/components/Pagination";
import { TableSkeleton } from "~/components/TableSkeleton";
import { type CVERecord, useCVEs } from "~/lib/cves";
import { severityLabel, severityPillClass } from "~/lib/iocVisuals";

const PAGE_SIZE = 50;

export function CVEsPage(): React.ReactElement {
    const [search, setSearch] = useState("");
    const [debounced, setDebounced] = useState("");
    const [minSeverity, setMinSeverity] = useState(1);
    const [ransomwareOnly, setRansomwareOnly] = useState(false);
    const [page, setPage] = useState(0);
    const [active, setActive] = useState<string | null>(null);

    useEffect(() => {
        const t = window.setTimeout(() => {
            setDebounced(search);
            setPage(0);
        }, 250);
        return () => window.clearTimeout(t);
    }, [search]);

    const offset = page * PAGE_SIZE;
    const result = useCVEs({
        search: debounced || undefined,
        minSeverity: minSeverity > 1 ? minSeverity : undefined,
        ransomwareOnly: ransomwareOnly || undefined,
        limit: PAGE_SIZE,
        offset,
    });
    const cves = result.data?.cves ?? [];
    const total = result.data?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const hasFilters = debounced || minSeverity > 1 || ransomwareOnly;

    const filterCount =
        (debounced ? 1 : 0) +
        (minSeverity > 1 ? 1 : 0) +
        (ransomwareOnly ? 1 : 0);

    return (
        <div className="stack-lg">
            <header className="page-header">
                <div className="page-header__top">
                    <div className="page-header__heading">
                        <span className="page-header__eyebrow">
                            <span
                                className="page-header__eyebrow__icon"
                                style={{ color: "var(--color-danger)" }}
                            >
                                <Bug size={14} aria-hidden />
                            </span>
                            Intelligence · Vulnerabilities
                        </span>
                        <h1 className="page-header__title">CVEs</h1>
                        <p className="page-header__description">
                            Known vulnerabilities tracked by the platform, currently fed by the
                            CISA KEV catalog (actively exploited in the wild). CVEs are intelligence
                            context — they link <em>to</em> IoCs but live in their own catalog.
                        </p>
                    </div>
                </div>
                <div className="page-header__meta">
                    <span className="page-header__meta__item tabular-nums">
                        {total.toLocaleString()} tracked
                    </span>
                    {filterCount > 0 && (
                        <span className="page-header__meta__item">
                            {filterCount} filter{filterCount === 1 ? "" : "s"} applied
                        </span>
                    )}
                    {!result.isFetching && result.dataUpdatedAt > 0 && (
                        <span className="page-header__meta__item">
                            Updated {new Date(result.dataUpdatedAt).toLocaleTimeString()}
                        </span>
                    )}
                </div>
            </header>

            <section className="section-card" aria-label="Filters">
                <div className="toolbar">
                    <div className="toolbar__search">
                        <Search size={14} className="toolbar__search__icon" aria-hidden />
                        <input
                            type="search"
                            className="input"
                            placeholder="Search CVE id, vendor, product, or name…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>

                    <select
                        className="input"
                        value={minSeverity}
                        onChange={(e) => {
                            setMinSeverity(Number(e.target.value));
                            setPage(0);
                        }}
                        style={{ width: 160, flexShrink: 0 }}
                        aria-label="Minimum severity"
                    >
                        {[1, 2, 4, 7, 9].map((n) => (
                            <option key={n} value={n}>
                                ≥ severity {n} {n > 1 ? `(${severityLabel(n)})` : ""}
                            </option>
                        ))}
                    </select>

                    <button
                        type="button"
                        onClick={() => {
                            setRansomwareOnly((v) => !v);
                            setPage(0);
                        }}
                        className={ransomwareOnly ? "chip chip--danger" : "chip"}
                        style={{ cursor: "pointer", height: 28, flexShrink: 0 }}
                        aria-pressed={ransomwareOnly}
                    >
                        {ransomwareOnly ? "✓ " : ""}Ransomware use only
                    </button>

                    {hasFilters && (
                        <div className="toolbar__actions">
                            <button
                                type="button"
                                className="btn btn--ghost btn--sm press-crush"
                                onClick={() => {
                                    setSearch("");
                                    setMinSeverity(1);
                                    setRansomwareOnly(false);
                                    setPage(0);
                                }}
                            >
                                <X size={12} aria-hidden /> Clear
                            </button>
                        </div>
                    )}
                </div>
            </section>

            <section className="section-card" aria-label="CVE results">
                {result.isLoading && <TableSkeleton rows={10} columns={5} />}
                {result.isError && (
                    <div className="empty-state">
                        <ShieldX size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">Could not load CVEs</div>
                        <div className="empty-state__body">
                            {result.error instanceof Error ? result.error.message : "Unknown error"}
                        </div>
                    </div>
                )}
                {result.data && cves.length === 0 && (
                    <div className="empty-state">
                        <Bug size={20} className="empty-state__icon" aria-hidden />
                        <div className="empty-state__title">No CVEs match your filters</div>
                        <div className="empty-state__body">
                            Loosen the filters or wait for the next CISA KEV pull (every 6 hours).
                        </div>
                    </div>
                )}
                {result.data && cves.length > 0 && (
                    <>
                        <div style={{ overflowX: "auto" }}>
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th style={{ whiteSpace: "nowrap", width: 1 }}>CVE</th>
                                        <th style={{ whiteSpace: "nowrap", width: 1 }}>Vendor / Product</th>
                                        <th>Name</th>
                                        <th style={{ whiteSpace: "nowrap", width: 1 }}>Severity</th>
                                        <th style={{ whiteSpace: "nowrap", width: 1 }}>CWE</th>
                                        <th style={{ whiteSpace: "nowrap", width: 1 }}>Date added</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cves.map((c) => (
                                        <CVERow key={c.id} cve={c} onOpen={() => setActive(c.id)} />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            total={total}
                            offset={offset}
                            shown={cves.length}
                            onChange={setPage}
                        />
                    </>
                )}
            </section>

            <CVEDetailDialog cveId={active} onClose={() => setActive(null)} />
        </div>
    );
}

function CVERow({ cve, onOpen }: { cve: CVERecord; onOpen: () => void }): React.ReactElement {
    return (
        <tr
            onClick={onOpen}
            style={{ cursor: "pointer" }}
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === "Enter") onOpen();
            }}
        >
            <td style={{ whiteSpace: "nowrap" }}>
                <div className="cluster-xs">
                    <span className="type-mono-sm" style={{ color: "var(--color-accent)" }}>
                        {cve.cveId}
                    </span>
                    {cve.ransomwareUse && (
                        <span className="chip chip--danger" style={{ height: 20 }}>ransomware</span>
                    )}
                </div>
            </td>
            <td className="text-13" style={{ whiteSpace: "nowrap" }}>
                <div className="stack-xs">
                    <span>{cve.vendorProject || "—"}</span>
                    {cve.product && (
                        <span className="text-12 text-text-muted">{cve.product}</span>
                    )}
                </div>
            </td>
            <td className="text-13" style={{ maxWidth: 0, width: "100%" }}>
                {/* max-width:0 + ellipsis — see iocs.tsx for canonical pattern. */}
                <span
                    title={cve.shortDescription || cve.name}
                    style={{
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {cve.name || cve.shortDescription || "—"}
                </span>
            </td>
            <td style={{ whiteSpace: "nowrap" }}>
                <span className={`status-pill ${severityPillClass(cve.severity)}`} style={{ borderColor: "transparent" }}>
                    {cve.severity} · {severityLabel(cve.severity)}
                </span>
            </td>
            <td style={{ whiteSpace: "nowrap" }}>
                {cve.cwes.length === 0 ? (
                    <span className="text-text-subtle text-12">—</span>
                ) : (
                    <div className="tag-stack" style={{ flexWrap: "nowrap" }}>
                        {cve.cwes.slice(0, 2).map((w) => (
                            <span key={w} className="chip" style={{ height: 20 }}>{w.toUpperCase()}</span>
                        ))}
                        {cve.cwes.length > 2 && (
                            <span className="tag-stack__more">+{cve.cwes.length - 2}</span>
                        )}
                    </div>
                )}
            </td>
            <td className="text-12 text-text-muted" style={{ whiteSpace: "nowrap" }}>
                {cve.dateAdded ?? "—"}
            </td>
        </tr>
    );
}
